import React from 'react';
import SEO from '../SEO';
import { seoConfig, generateBreadcrumbJsonLd } from '../../utils/seoData';

const PrivacyPolicyPage: React.FC = () => {
  const breadcrumbJsonLd = generateBreadcrumbJsonLd([
    { name: 'Ana Sayfa', url: 'https://winnomi.com' },
    { name: 'Gizlilik Politikası', url: 'https://winnomi.com/gizlilik-politikasi' },
  ]);

  return (
    <>
      <SEO
        {...seoConfig.privacy}
        jsonLd={[
          breadcrumbJsonLd,
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Gizlilik Politikası',
            description: seoConfig.privacy.description,
            url: 'https://winnomi.com/gizlilik-politikasi',
            datePublished: '2025-11-09',
            dateModified: '2025-11-09',
          },
        ]}
      />
      <main className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-5xl mx-auto px-6 py-24 space-y-12">
        <header className="space-y-4">
          <p className="text-sm uppercase tracking-widest text-purple-300">Gizlilik Politikası</p>
          <h1 className="text-3xl md:text-4xl font-semibold text-white">
            Winnomi AI Gizlilik Politikası
          </h1>
          <p className="text-slate-400">Güncelleme tarihi: 09 Kasım 2025</p>
          <p className="text-slate-300 leading-relaxed">
            TARIK ÇEKEMOĞLU CONCEPTION TEAM DANIŞMANLIK HİZMETLERİ (“Şirket”) olarak Winnomi AI platformu
            (“Platform”) kullanıcılarının gizliliğini korumayı taahhüt ediyoruz. Bu Gizlilik Politikası, hangi
            verileri topladığımızı, nasıl işlediğimizi, ne kadar süre sakladığımızı ve verilerinize ilişkin haklarınızı
            açıklar.
          </p>
        </header>

        <section className="space-y-6">
          <h2 className="text-2xl font-semibold text-white">1. Toplanan Veriler</h2>
          <div className="space-y-4 text-slate-300 leading-relaxed">
            <p>Platform kullanımınız sırasında aşağıdaki veri grupları toplanabilir:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-300">
              <li>Kimlik verileri (ad, soyad, kullanıcı ID)</li>
              <li>İletişim verileri (e-posta, telefon, adres)</li>
              <li>Hesap ve profil verileri (şirket adı, sektör, sosyal medya hesapları)</li>
              <li>Kullanım verileri (oturum bilgileri, tıklama geçmişi, sayfa görüntüleme istatistikleri)</li>
              <li>Cihaz ve teknik veriler (IP adresi, tarayıcı türü, işletim sistemi, çerezler)</li>
              <li>Ödeme ve faturalama verileri (fatura bilgileri, ödeme kayıtları)</li>
              <li>İletişim ve destek kayıtları (destek talepleri, çağrı merkezi ses kayıtları)</li>
              <li>Üçüncü taraf entegrasyon verileri (API tokent’ları, sosyal medya metrikleri)</li>
            </ul>
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-semibold text-white">2. Çerezler ve Benzeri Teknolojiler</h2>
          <div className="space-y-4 text-slate-300 leading-relaxed">
            <p>
              Platform, kullanıcı deneyimini geliştirmek, performansı ölçmek ve kişiselleştirilmiş içerik sunmak için
              çerezler ve benzeri teknolojiler kullanır. Çerezler tarayıcıya yerleştirilen küçük metin dosyalarıdır.
              Kullanılan çerez türleri:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-300">
              <li><strong className="text-white">Zorunlu çerezler:</strong> Platform’un temel fonksiyonlarını sağlar.</li>
              <li><strong className="text-white">Performans çerezleri:</strong> Kullanıcı davranışlarını anonim şekilde analiz eder.</li>
              <li><strong className="text-white">Pazarlama çerezleri:</strong> Açık rızaya tabi olarak kişiselleştirilmiş reklam içerikleri sunar.</li>
            </ul>
            <p>
              Tarayıcı ayarlarınızdan çerez tercihlerinizi değiştirebilir veya çerezleri silebilirsiniz. Çerezlerin
              devre dışı bırakılması bazı özelliklerin kullanımını sınırlayabilir.
            </p>
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-semibold text-white">3. Veri İşleme Amaçları</h2>
          <div className="space-y-4 text-slate-300 leading-relaxed">
            <p>Toplanan veriler aşağıdaki amaçlarla işlenmektedir:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-300">
              <li>Platform hizmetlerinin sunulması ve geliştirilmesi</li>
              <li>Kullanıcı hesaplarının oluşturulması, yönetimi ve güvenliği</li>
              <li>Performans analizi, raporlama ve ürün geliştirme</li>
              <li>Yasal yükümlülüklerin yerine getirilmesi ve denetim süreçleri</li>
              <li>Müşteri desteği, iletişim ve şikayet yönetimi</li>
              <li>Pazarlama iletişimi ve kullanıcı segmentasyonu (açık rıza ile)</li>
              <li>Dolandırıcılık ve kötüye kullanımın tespit edilmesi</li>
            </ul>
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-semibold text-white">4. Veri Paylaşımı</h2>
          <div className="space-y-4 text-slate-300 leading-relaxed">
            <p>
              Kişisel verileriniz, hizmetlerin ifası için gerekli olduğu ölçüde ve KVKK’ya uygun şekilde aşağıdaki
              taraflarla paylaşılabilir:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-300">
              <li>Teknoloji altyapısı ve barındırma hizmeti sağlayıcıları</li>
              <li>Ödeme kuruluşları, bankalar ve finansal danışmanlar</li>
              <li>İş ortakları, entegrasyon sağlayıcıları ve alt yükleniciler</li>
              <li>Hukuken yetkili kamu kurumları ve düzenleyici otoriteler</li>
              <li>Yurtdışı veri aktarımı gereken durumlarda yeterlilik kararlarına ve açık rızaya tabidir</li>
            </ul>
            <p>Veri paylaşımı yapılırken veri güvenliği sözleşmeleri ve gerekli teknik-idari tedbirler uygulanır.</p>
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-semibold text-white">5. Veri Saklama ve Silme Politikası</h2>
          <div className="space-y-4 text-slate-300 leading-relaxed">
            <p>
              Verileriniz, işleme amaçlarının gerektirdiği süre boyunca saklanır; süre sonunda mevzuata uygun şekilde
              silinir, yok edilir veya anonim hale getirilir. Saklama süreleri, KVKK Aydınlatma Metni’nde detaylandırılmıştır.
            </p>
            <p>
              Verilerin silinmesi, yok edilmesi veya anonim hale getirilmesine ilişkin taleplerinizi info@winnomi.com
              adresine iletebilirsiniz.
            </p>
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-semibold text-white">6. Veri Güvenliği</h2>
          <div className="space-y-4 text-slate-300 leading-relaxed">
            <p>
              Şirket, yetkisiz erişim, kayıp, kötüye kullanım veya ifşa risklerine karşı kişisel verilerinizi korumak için
              teknik ve idari tedbirler uygular:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-300">
              <li>SSL/TLS ile şifrelenmiş veri aktarımı</li>
              <li>Role dayalı erişim kontrolü ve çok faktörlü kimlik doğrulama</li>
              <li>Düzenli zafiyet taraması ve sızma testleri</li>
              <li>Yedekleme, felaket kurtarma planları ve loglama</li>
              <li>Çalışan gizlilik sözleşmeleri ve KVKK eğitimleri</li>
            </ul>
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-semibold text-white">7. Üçüncü Taraf Bağlantıları</h2>
          <div className="space-y-4 text-slate-300 leading-relaxed">
            <p>
              Platform, üçüncü taraf web sitelerine veya uygulamalarına bağlantılar içerebilir. Üçüncü taraf sitelerin
              gizlilik uygulamalarından Şirket sorumlu değildir; bu siteleri ziyaret etmeden önce ilgili gizlilik
              politikalarını incelemenizi öneririz.
            </p>
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-semibold text-white">8. Çocukların Gizliliği</h2>
          <div className="space-y-4 text-slate-300 leading-relaxed">
            <p>
              Platform 18 yaşından küçük kişiler için tasarlanmamıştır. Ebeveyn izni olmadan 18 yaş altı kullanıcıların
              verilerini bilerek toplamıyoruz. Böyle bir durum tespit edilirse veriler derhal silinir.
            </p>
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-semibold text-white">9. Haklarınız ve Başvuru</h2>
          <div className="space-y-4 text-slate-300 leading-relaxed">
            <p>
              KVKK kapsamındaki haklarınızı (veriye erişim, düzeltme, silme, işleme itiraz vb.) kullanmak için
              info@winnomi.com adresine veya Şirket adresine yazılı başvuruda bulunabilirsiniz. Başvurular 30 gün
              içinde sonuçlandırılır.
            </p>
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-semibold text-white">10. Politika Değişiklikleri</h2>
          <div className="space-y-4 text-slate-300 leading-relaxed">
            <p>
              Bu Gizlilik Politikası, mevzuat değişiklikleri veya hizmetlerdeki güncellemeler doğrultusunda
              revize edilebilir. Güncel tarihli politika Platform’da yayınlanır ve yürürlüğe girer. Önemli değişiklikler
              kullanıcıya e-posta veya uygulama içi bildirim yoluyla iletilebilir.
            </p>
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-semibold text-white">11. İletişim</h2>
          <div className="space-y-4 text-slate-300 leading-relaxed">
            <p>
              Gizlilikle ilgili sorularınız için bizimle iletişime geçebilirsiniz:
            </p>
            <div className="space-y-1">
              <p><strong className="text-white">E-posta:</strong> info@winnomi.com</p>
              <p><strong className="text-white">Telefon:</strong> +90 (546) 597 56 43</p>
              <p><strong className="text-white">Adres:</strong> MASLAK MAH. AOS 55. SK. 42 MASLAK A BLOK NO: 2 İÇ KAPI NO: 25 SARIYER / İSTANBUL PK: 34475</p>
            </div>
          </div>
        </section>
      </div>
    </main>
    </>
  );
};

export default PrivacyPolicyPage;

